import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { expensePaymentApi, expenseApi } from '../../services/api';

const ExpensePaymentForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = !!id;

  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const expenseIdFromQuery = queryParams.get('expense_id');
  const amountFromQuery = queryParams.get('amount');

  const [formData, setFormData] = useState({
    expense_id: expenseIdFromQuery || '',
    amount: amountFromQuery || '',
    paid_on: new Date().toISOString().split('T')[0],
    valid_from: '',
    valid_until: ''
  });
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch all expenses
        const expensesResponse = await expenseApi.getAll();
        setExpenses(expensesResponse.data);
        
        // If editing, fetch payment details
        if (isEditMode) {
          const paymentResponse = await expensePaymentApi.getById(id);
          const payment = paymentResponse.data;
          
          setFormData({
            expense_id: payment.expense_id,
            amount: payment.amount,
            paid_on: payment.paid_on ? new Date(payment.paid_on).toISOString().split('T')[0] : '',
            valid_from: payment.valid_from ? new Date(payment.valid_from).toISOString().split('T')[0] : '',
            valid_until: payment.valid_until ? new Date(payment.valid_until).toISOString().split('T')[0] : ''
          });
        } else if (!expenseIdFromQuery && expensesResponse.data.length > 0) {
          // Set default expense if creating new payment and no expense_id in query params
          setFormData(prev => ({
            ...prev,
            expense_id: expensesResponse.data[0].id
          }));
        }
        
        setLoading(false);
      } catch (err) {
        setError('Failed to load data');
        setLoading(false);
        console.error(err);
      }
    };

    fetchData();
  }, [id, isEditMode, expenseIdFromQuery]);
  
  // Define calculatePeriod function
  const calculatePeriod = useCallback(() => {
    if (!formData.expense_id || !formData.paid_on) return;
    
    const expense = expenses.find(e => e.id === formData.expense_id);
    if (!expense) return;
    
    const paidOn = new Date(formData.paid_on);
    let validFrom = new Date(paidOn);
    validFrom.setDate(1); // First day of the month
    
    let validUntil;
    
    switch (expense.interval) {
      case 'monthly':
        validUntil = new Date(validFrom);
        validUntil.setMonth(validUntil.getMonth() + 1);
        validUntil.setDate(0); // Last day of the month
        break;
      case 'quarterly':
        validUntil = new Date(validFrom);
        validUntil.setMonth(validUntil.getMonth() + 3);
        validUntil.setDate(0); // Last day of the month
        break;
      case 'yearly':
        validUntil = new Date(validFrom);
        validUntil.setFullYear(validUntil.getFullYear() + 1);
        validUntil.setDate(0); // Last day of the month
        break;
      case 'one-time':
        validUntil = new Date(validFrom);
        validUntil.setFullYear(validUntil.getFullYear() + 1);
        validUntil.setDate(0); // Last day of the month
        break;
      default:
        return;
    }
    
    setFormData(prev => ({
      ...prev,
      valid_from: validFrom.toISOString().split('T')[0],
      valid_until: validUntil.toISOString().split('T')[0]
    }));
  }, [formData.expense_id, formData.paid_on, expenses]);
  
  // Calculate period automatically when the form loads with expense_id and amount from query params
  useEffect(() => {
    if (!loading && expenses.length > 0 && expenseIdFromQuery && amountFromQuery) {
      calculatePeriod();
    }
  }, [loading, expenses, expenseIdFromQuery, amountFromQuery, calculatePeriod]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Convert string values to appropriate types and remove empty fields
      const processedData = {
        expense_id: formData.expense_id,
        amount: parseFloat(formData.amount),
        paid_on: formData.paid_on
      };
      
      // Only include optional date fields if they have a value
      if (formData.valid_from) {
        processedData.valid_from = formData.valid_from;
      }
      
      if (formData.valid_until) {
        processedData.valid_until = formData.valid_until;
      }
      
      if (isEditMode) {
        await expensePaymentApi.update(id, processedData);
      } else {
        await expensePaymentApi.create(processedData);
      }
      navigate('/expense-payments');
    } catch (err) {
      setError('Failed to save expense payment');
      console.error(err);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col">
          <h1 className="display-5 mb-3">{isEditMode ? 'Edit Expense Payment' : 'Record New Expense Payment'}</h1>
          
          {error && <div className="alert alert-danger">{error}</div>}
        </div>
      </div>
      
      <div className="row">
        <div className="col-md-8 col-lg-6">
          <div className="card shadow-sm">
            <div className="card-body p-4">
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="expense_id" className="form-label fw-bold">Expense</label>
                  <select
                    className="form-select"
                    id="expense_id"
                    name="expense_id"
                    value={formData.expense_id}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select an expense</option>
                    {expenses.map(expense => (
                      <option key={expense.id} value={expense.id}>
                        {expense.name} ({expense.category})
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="mb-3">
                  <label htmlFor="amount" className="form-label fw-bold">Amount (€)</label>
                  <div className="input-group">
                    <span className="input-group-text">€</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="form-control"
                      id="amount"
                      name="amount"
                      value={formData.amount}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                
                <div className="mb-3">
                  <label htmlFor="paid_on" className="form-label fw-bold">Payment Date</label>
                  <input
                    type="date"
                    className="form-control"
                    id="paid_on"
                    name="paid_on"
                    value={formData.paid_on}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <hr className="my-4" />
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Validity Period</h5>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={calculatePeriod}
                  >
                    <i className="fas fa-calculator me-2"></i>
                    Calculate Period
                  </button>
                </div>
                <div className="form-text mb-3">
                  You can manually set the period or click the button to automatically calculate it based on the expense's interval.
                </div>
                
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="valid_from" className="form-label">Valid From</label>
                    <input
                      type="date"
                      className="form-control"
                      id="valid_from"
                      name="valid_from"
                      value={formData.valid_from}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <div className="col-md-6 mb-4">
                    <label htmlFor="valid_until" className="form-label">Valid Until</label>
                    <input
                      type="date"
                      className="form-control"
                      id="valid_until"
                      name="valid_until"
                      value={formData.valid_until}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                
                <div className="d-flex gap-2">
                  <button type="submit" className="btn btn-success px-4">
                    <i className="fas fa-save me-2"></i>
                    {isEditMode ? 'Update Payment' : 'Record Payment'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/expense-payments')}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpensePaymentForm;
